import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { DriehoekData, TriangleItem } from "../types/driehoek-data";
import { clampToSector, resolveDropPosition } from "../lib/geometry";
import {
  itemsPositionsChanged,
  readPositionsFromDOM,
  resolveCollisions,
} from "../lib/collision";
import { LabelDebugOverlay } from "./LabelDebugOverlay";

interface TriangleCanvasProps {
  data: DriehoekData;
  onItemsChange: (items: TriangleItem[]) => void;
  printMode?: boolean;
  showLabelDebug?: boolean;
  onLoadDebugFixture?: () => void;
}

function itemsLayoutKey(items: TriangleItem[]): string {
  return items.map((i) => `${i.id}:${i.text}:${i.factor}:${i.x}:${i.y}`).join("|");
}

export function TriangleCanvas({
  data,
  onItemsChange,
  printMode,
  showLabelDebug,
  onLoadDebugFixture,
}: TriangleCanvasProps) {
  const layerRef = useRef<HTMLDivElement>(null);
  const dragIdRef = useRef<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const isMobile =
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

  dragIdRef.current = dragId;

  const runCollisionResolve = useCallback(() => {
    const container = layerRef.current;
    if (!container || data.items.length < 2 || dragIdRef.current) return;

    resolveCollisions(container);
    const updated = readPositionsFromDOM(container, data.items);
    if (itemsPositionsChanged(data.items, updated)) {
      onItemsChange(updated);
    }
  }, [data.items, onItemsChange]);

  const layoutKey = itemsLayoutKey(data.items);

  useLayoutEffect(() => {
    if (dragIdRef.current) return;
    const frame = requestAnimationFrame(runCollisionResolve);
    return () => cancelAnimationFrame(frame);
  }, [layoutKey, printMode, runCollisionResolve]);

  useEffect(() => {
    const onResize = () => {
      requestAnimationFrame(runCollisionResolve);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [runCollisionResolve]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const id = e.dataTransfer.getData("text/plain") || dragId;
      if (!id || !layerRef.current) return;
      const rect = layerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 400;
      const y = ((e.clientY - rect.top) / rect.height) * 400;
      const pos = resolveDropPosition(x, y);
      if (!pos) return;
      onItemsChange(
        data.items.map((it) =>
          it.id === id ? { ...it, factor: pos.factor, x: pos.x, y: pos.y } : it
        )
      );
      setDragId(null);
    },
    [data.items, dragId, onItemsChange]
  );

  const basisText = (data.basisgegevens || "").trim();

  return (
    <div className="cd-triangle-wrap">
      <div
        className={`cd-triangle-container ${printMode ? "cd-print-mode" : ""} ${
          showLabelDebug ? "cd-debug-mode" : ""
        }`}
      >
      {basisText ? (
        <p className="cd-basisgegevens-overlay">{basisText}</p>
      ) : null}
      <svg className="cd-triangle-svg" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid meet">
        <polygon
          className="cd-sector cd-sector-omgeving cd-sector-outer"
          points="0,0 200,0 200,82 74,301 0,301"
        />
        <polygon
          className="cd-sector cd-sector-gezin cd-sector-outer"
          points="400,0 200,0 200,82 326,301 400,301"
        />
        <polygon
          className="cd-sector cd-sector-kind cd-sector-outer"
          points="0,301 400,301 400,400 0,400"
        />
        <polygon
          className="cd-sector cd-sector-omgeving cd-sector-inner"
          points="200,82 74,301 112,279 200,126"
        />
        <polygon
          className="cd-sector cd-sector-gezin cd-sector-inner"
          points="200,82 326,301 288,279 200,126"
        />
        <polygon
          className="cd-sector cd-sector-kind cd-sector-inner"
          points="74,301 326,301 288,279 112,279"
        />
        <polygon className="cd-triangle-outline" points="200,82 326,301 74,301" />
        <polygon className="cd-triangle-center" points="200,126 112,279 288,279" />
        <text x="200" y="228" className="cd-label cd-label-center" textAnchor="middle">
          <tspan x="200" dy="0">
            Wat verklaart hoe het
          </tspan>
          <tspan x="200" dy="1.15em">
            met dit kind gaat?
          </tspan>
        </text>
        <text
          x="159"
          y="184"
          className="cd-label cd-label-sector cd-label-sector-omgeving"
          textAnchor="middle"
          transform="rotate(300 165 197)"
        >
          Ervaringen &amp; omgeving
        </text>
        <text
          x="242"
          y="184"
          className="cd-label cd-label-sector cd-label-sector-gezin"
          textAnchor="middle"
          transform="rotate(60 235 197)"
        >
          Gezinscontext &amp; opvoeding
        </text>
        <text
          x="200"
          y="294"
          className="cd-label cd-label-sector cd-label-sector-kind"
          textAnchor="middle"
        >
          Ontwikkeling Kind
        </text>
      </svg>
      {showLabelDebug && <LabelDebugOverlay onLoadFixture={onLoadDebugFixture} />}
      <div
        ref={layerRef}
        className="cd-items-layer"
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
        }}
        onDrop={handleDrop}
      >
        {data.items.map((item) => {
          const [cx, cy] = clampToSector(item.x, item.y, item.factor);
          return (
            <div
              key={item.id}
              className={`cd-triangle-item cd-triangle-item-${item.factor} ${
                dragId === item.id ? "dragging" : ""
              }`}
              style={{
                left: `${(cx / 400) * 100}%`,
                top: `${(cy / 400) * 100}%`,
              }}
              data-id={item.id}
              data-factor={item.factor}
              data-x={cx}
              data-y={cy}
              draggable={!isMobile}
              onDragStart={(e) => {
                setDragId(item.id);
                e.dataTransfer.setData("text/plain", item.id);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragEnd={() => setDragId(null)}
            >
              {item.text}
            </div>
          );
        })}
      </div>
      </div>
      {!isMobile && !printMode ? (
        <p className="cd-drag-hint">
          {data.items.length > 0
            ? "Sleep labels om ze te verplaatsen in de driehoek."
            : "Voeg factoren toe via het invulpaneel; sleep ze daarna naar de gewenste plek."}
        </p>
      ) : null}
    </div>
  );
}
